import {
  IconDatabase,
  IconEgress,
  IconKube,
  IconModel,
  IconPort,
  IconResource,
  IconSsh,
  IconTool,
  IconWeb,
} from './icons';

/**
 * The picture that goes in front of a resource's name.
 *
 * One glance tells a reader which of nine things a row is, without their
 * having to read the kind column beside it — which is the whole reason the
 * catalogue can afford to keep that column quiet.
 *
 * The two web kinds get two pictures rather than one, for the same reason
 * gdsgate makes them two kinds: an application behind the gateway and a way out
 * of the protected zone are decided over different Cedar types, and a reader
 * scanning a catalogue must not have to read the word to tell them apart.
 *
 * A kind neither side knows draws the neutral square. Not the nearest-looking
 * one: a wrong picture is read faster than a wrong word and believed longer,
 * and the day gdsgate learns a tenth kind this console must not claim it is
 * something else.
 */
/**
 * The hue each kind is drawn in — the reference build's own palette, expressed
 * in this console's tokens because they are the same five values it already
 * uses for accent, warning, purple, ok and deny.
 *
 * Decoration, and only that: no state is carried here. Two kinds share the
 * accent hue in the reference build and they share it here, which is the proof
 * — a colour that meant something could not be reused like that.
 */
const HUE: Record<string, string> = {
  ssh: 'var(--accent)',
  postgres: 'var(--accent)',
  kubernetes: 'var(--accent)',
  mysql: 'var(--warn)',
  mcp: 'var(--violet)',
  tcp: 'var(--ok)',
  llm: 'var(--deny)',
  web: 'var(--accent)',
  'web-egress': 'var(--warn)',
};

const BY_KIND: Record<string, (props: { size?: number }) => React.ReactElement> = {
  ssh: IconSsh,
  postgres: IconDatabase,
  mysql: IconDatabase,
  kubernetes: IconKube,
  tcp: IconPort,
  mcp: IconTool,
  llm: IconModel,
  web: IconWeb,
  'web-egress': IconEgress,
};

export function KindIcon({ kind, size }: { kind: string; size?: number }) {
  const Icon = BY_KIND[kind] ?? IconResource;
  return (
    <span className="res-ico" style={{ color: HUE[kind] ?? 'var(--text-dim)' }}>
      <Icon {...(size === undefined ? {} : { size })} />
    </span>
  );
}
